import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CandidateInvitations } from "@/components/CandidateInvitations";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Link2, Loader2, TriangleAlert, Users, Globe } from "lucide-react";
import { sectionPath } from "@/components/job/Lifecycle";

function Channel({ checked, title, desc, right, children, testId }) {
  return (
    <div className={`rounded-xl border p-5 ${checked ? "border-primary/40 bg-primary/[0.04]" : "border-border bg-card"}`} data-testid={testId}>
      <div className="flex items-start gap-4">
        <span className={`mt-0.5 h-5 w-5 rounded-md flex items-center justify-center shrink-0 ${checked ? "bg-primary text-primary-foreground" : "border border-foreground/25"}`}>{checked && <Check className="h-3.5 w-3.5" />}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{title}</div>
          <div className="text-sm text-muted-foreground">{desc}</div>
        </div>
        {right && <div className="text-sm text-muted-foreground shrink-0">{right}</div>}
      </div>
      {children}
    </div>
  );
}

export function PublishStep({ job, onPublish, publishing }) {
  const nav = useNavigate();
  const [gate, setGate] = useState(null);
  const [company, setCompany] = useState(null);
  const [showInvites, setShowInvites] = useState(false);
  const live = job.status === "published";
  const lc = job.lifecycle || {};
  const url = `${window.location.origin}/careers/${job.id}`;
  useEffect(() => {
    api.get(`/jobs/${job.id}/publish-gate`).then((r) => setGate(r.data)).catch(() => {});
    api.get("/company/profile").then((r) => setCompany(r.data)).catch(() => {});
  }, [job.id]);

  return (
    <div className="app-container py-8 grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_420px] gap-8" data-testid="publish-step">
      <div className="space-y-4 min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-display font-semibold">Where should this role appear?</h2>
          {!live && <Button onClick={onPublish} disabled={publishing || !gate?.approved} className="gap-2 font-semibold" data-testid="publish-now">
            {publishing && <Loader2 className="h-4 w-4 animate-spin" />}Publish role<ArrowRight className="h-4 w-4" /></Button>}
        </div>
        {!live && gate && !gate.approved && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-4 flex flex-wrap items-center gap-3" data-testid="publish-gate-notice">
            <TriangleAlert className="h-4 w-4 text-amber-500 shrink-0" />
            <p className="text-sm flex-1 min-w-[16rem]">Before going live, {gate.has_trial ? "approve the trial report" : "run a trial of the assessment and approve it"}.</p>
            <button onClick={() => nav(gate.has_trial && gate.latest_trial_id ? `/recruiter/trial-report/${gate.latest_trial_id}` : sectionPath(job.id, "test"))} className="text-sm font-medium text-primary hover:underline" data-testid="publish-gate-open">{gate.has_trial ? "Open trial report" : "Go to assessment"} →</button>
          </div>
        )}
        {(gate?.warnings || []).map((w) => <p key={w} className="text-xs text-amber-700 dark:text-amber-300">{w}</p>)}
        <Channel checked title="Your careers page" desc="Always on for published roles. Candidates apply, consent and take the assessment here." right={live ? "Live" : "On publish"} testId="channel-careers" />
        <Channel checked title="Assessment" testId="channel-assessment"
          desc={lc.active_version ? `Candidates take assessment v${lc.active_version}.` : lc.latest_version ? `Assessment v${lc.latest_version} goes live with this role.` : "Candidates get a fair test composed from your question bank."}
          right={<button onClick={() => nav(sectionPath(job.id, "test"))} className="text-primary font-medium hover:underline" data-testid="channel-assessment-open">Review</button>} />
        <Channel checked={false} title="Private assessment invites" desc="One-to-one sign-in links for a cohort, campus drive or referrals." testId="channel-invites"
          right={<button onClick={() => setShowInvites((v) => !v)} className="text-primary font-medium hover:underline" data-testid="channel-invites-toggle">{showInvites ? "Hide" : "Manage"}</button>}>
          {showInvites && <div className="mt-4"><CandidateInvitations /></div>}
        </Channel>
      </div>
      <aside className="space-y-3 min-w-0" aria-label="Careers page preview">
        <div className="text-sm text-muted-foreground font-medium">Preview on your careers page</div>
        <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="publish-preview-card">
          <div className="text-xs text-muted-foreground">{company?.name || job.company_name || "Your company"}{job.department ? ` · ${job.department}` : ""}</div>
          <div className="text-xl font-display font-semibold">{job.title}</div>
          <div className="flex flex-wrap gap-1.5">
            {[job.location, job.seniority, job.employment_type].filter(Boolean).map((t) => <span key={t} className="text-xs px-2 py-1 rounded-md bg-foreground/[0.06]">{t}</span>)}
          </div>
          {job.summary && <p className="text-sm text-muted-foreground line-clamp-3">{job.summary}</p>}
          <div className="rounded-lg bg-primary text-primary-foreground text-sm font-semibold text-center py-2.5 opacity-90" aria-hidden="true">Apply</div>
        </div>
        <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3" data-testid="publish-link">
          {live ? <Link2 className="h-4 w-4 text-primary shrink-0" /> : <Globe className="h-4 w-4 text-muted-foreground shrink-0" />}
          <span className="font-mono text-xs truncate flex-1 min-w-0" data-testid="publish-link-url">{live ? url.replace(/^https?:\/\//, "") : "Your public link appears once the role is live"}</span>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Every applicant is scored with explainable evidence, and a person makes every decision.</p>
      </aside>
    </div>
  );
}
